/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { hash } from "../../utils/hash.js";
import { SnapshotUpdater, } from "../../utils/snapshot-updater.js";
export { DescribeResultCache };
class DescribeResultCache {
    args;
    #map = new Map();
    constructor(args) {
        this.args = args;
    }
    #createSnapshotArgs(graphId, nodeId, inputs) {
        return {
            initial: () => this.args.initial(graphId, nodeId),
            latest: () => this.args.latest(graphId, nodeId, inputs),
            willUpdate: (previous, current) => this.args.willUpdate(previous, current),
            updated: () => {
                this.args.updated(graphId, nodeId);
            },
        };
    }
    #createInertSnapshotArgs(graphId, nodeId, inputs) {
        return {
            initial: () => this.args.initial(graphId, nodeId),
            latest: () => this.args.latest(graphId, nodeId, inputs),
            willUpdate() { },
        };
    }
    getByType(type) {
        const typeHash = hash(type);
        let result = this.#map.get(typeHash);
        if (result) {
            return result.snapshot();
        }
        result = new SnapshotUpdater({
            initial: () => this.args.initialType(),
            latest: () => this.args.latestType(type),
            updated: () => this.args.updatedType(type),
        });
        this.#map.set(typeHash, result);
        return result.snapshot();
    }
    get(id, graphId, inputs) {
        if (inputs && Object.keys(inputs).length > 0) {
            // bypass cache when there are inputs. We can't cache these
            // describer results ... yet.
            return new SnapshotUpdater(this.#createInertSnapshotArgs(graphId, id, inputs)).snapshot();
        }
        const hash = computeHash({ id, graphId });
        let result = this.#map.get(hash);
        if (result) {
            return result.snapshot();
        }
        result = new SnapshotUpdater(this.#createSnapshotArgs(graphId, id));
        this.#map.set(hash, result);
        return result.snapshot();
    }
    update(affectedNodes) {
        affectedNodes.forEach((affected) => {
            const hash = computeHash(affected);
            this.#map.get(hash)?.refresh();
        });
    }
    clear(visualOnly, affectedNodes) {
        if (visualOnly) {
            return;
        }
        affectedNodes.forEach((node) => {
            const hash = computeHash(node);
            this.#map.delete(hash);
        });
    }
}
function computeHash(node) {
    return hash(node);
}
//# sourceMappingURL=describe-cache.js.map